import {
  deserify,
  getMediaBasePath,
  getTeacherSubjectEnum,
} from '@geneo2-web/shared-ui';
import { Subject_SubjectEnum } from '@protos/common/common.db_pb';
import {
  MultiSubjectFormateInfo,
  SubSectionQuestionType,
  SubSectionType,
  TaskInfoModel,
} from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { AssessmentContent } from '@protos/learning_management/lms.assessment.common.apis_pb';
import {
  ChapterIdsInfo,
  MultiSubjectSection,
  MultiSubjectSectionTemplate,
  QuestionSectionMeta,
  SectionQuestionMetaData,
  SubSectionQuestionTemplate,
  SubSectionTemplate,
  TaskInfo,
  TopicIdsInfo,
} from '@protos/learning_management/lms.db_pb';
import { TeachClassSubjects } from '@protos/user_management/ums.login.apis_pb';
import { useAppDispatch, useAppSelector } from '../../../reduxStore/reduxHooks';
import {
  setQPGAssessmentContent,
  setQPGQuestionModuleMap,
  setQPGQuestionsSequenceInfo,
} from '../reducer/assessmentFlow.slice';
import {
  Question,
  Question_QuestionEnum,
} from '@protos/content_management/content.db_pb';
import { QuestionTypeCount } from '@protos/learning_management/lms.hw.teacher.apis_pb';

// =============================================================

/**
 * Builds a MultiSubjectSectionTemplate from the current section and subsection data
 * for use in createQuestionPaperTemplate (saveSectionTemplates).
 */
export const createMultiSubjectSectionTemplate = (props: {
  multiSubjectSection: MultiSubjectSection | undefined;
  subsectionData: SubSectionType[];
}): MultiSubjectSectionTemplate => {
  const { multiSubjectSection, subsectionData } = props;
  const sectionTittle =
    multiSubjectSection?.sectionTitle || multiSubjectSection?.sectionName || '';

  let noOfQuestions = 0;
  let marks = 0;

  const subSectionTemplate: SubSectionTemplate[] = (subsectionData ?? []).map(
    (sub) => {
      const subSectionQuestionTemplate: SubSectionQuestionTemplate[] = (
        sub.subSectionQuestions ?? []
      ).map(
        (q: SubSectionQuestionType) =>
          new SubSectionQuestionTemplate({
            questionType: q.questionType,
            noOfQuestion: q.noOfQuestion ?? 0,
            marks: q.marks,
            isAlternateEnable: q.isAlternateEnable ?? false,
          })
      );

      const subNoOfQuestions = subSectionQuestionTemplate.reduce(
        (sum, q) => sum + (q.noOfQuestion ?? 0),
        0
      );
      const subMarks = subSectionQuestionTemplate.reduce(
        (sum, q) => sum + (q.noOfQuestion ?? 0) * (q.marks ?? 0),
        0
      );
      noOfQuestions += subNoOfQuestions;
      marks += subMarks;

      return new SubSectionTemplate({
        subSectionTittle: sub.subSectionName ?? '',
        subSectionNumber: sub.sectionNumber,
        subSectionQuestionTemplate,
      });
    }
  );

  return new MultiSubjectSectionTemplate({
    sectionTittle: sectionTittle || undefined,
    noOfQuestions,
    marks,
    subSectionTemplate,
  });
};

// =============================================================

/**
 * Returns unique question IDs from all sections of qpg_content
 * (main questions and alternate questions in every subSectionMetaData).
 */
export const getUniqueQuestionIdsFromQpgContent = (props: {
  qpgContent: AssessmentContent | undefined;
  sectionNumbersToIgnore?: number[];
}): string[] => {
  const { qpgContent, sectionNumbersToIgnore } = props;
  if (!qpgContent?.multiSubjectSections?.length) return [];

  const ignoreSet = sectionNumbersToIgnore?.length
    ? new Set(sectionNumbersToIgnore)
    : undefined;

  const ids = new Set<string>();
  for (const section of qpgContent.multiSubjectSections) {
    if (ignoreSet?.has(section.sectionNo ?? 0)) continue;

    for (const subSection of section.multiSubjectSubSections ?? []) {
      for (const meta of subSection.subSectionMetaData ?? []) {
        if (meta.questionId) ids.add(meta.questionId);

        for (const alt of meta.alternateQuestions ?? []) {
          if (alt.questionId) ids.add(alt.questionId);
        }
      }
    }
  }
  return Array.from(ids);
};

// =============================================================

/**
 * Returns total marks of a section by summing maxMarks from all subSectionMetaData.
 */
export const getTotalMarksOfSection = (
  section: MultiSubjectSection
): number => {
  return (section.multiSubjectSubSections ?? []).reduce(
    (sectionTotal, subSection) =>
      sectionTotal +
      (subSection.subSectionMetaData ?? []).reduce(
        (subTotal, meta) => subTotal + (meta.maxMarks ?? 0),
        0
      ),
    0
  );
};

// =============================================================

export const getSubjectImageBySubjectId = (
  subjectId?: number,
  teachClassSubjects?: TeachClassSubjects[]
): string => {
  if (subjectId == null || !teachClassSubjects?.length) return '';
  for (const classData of teachClassSubjects) {
    for (const subjectData of classData.subjects ?? []) {
      if (subjectData.subjectId === subjectId && subjectData.iconUrl) {
        return getMediaBasePath(
          subjectData.iconUrl,
          'schoolnetStaticAssetBucket'
        );
      }
    }
  }
  return '';
};

// =============================================================

export const getSubjectColorBySubjectId = (
  subjectId?: number,
  teachClassSubjects?: TeachClassSubjects[]
): string => {
  if (subjectId == null || !teachClassSubjects?.length) return '';
  for (const classData of teachClassSubjects) {
    for (const subjectData of classData.subjects ?? []) {
      if (subjectData.subjectId === subjectId && subjectData.iconUrl) {
        return `${subjectData.textColor || '#000000'}33`;
      }
    }
  }
  return '';
};

// =============================================================

/* Returns chapters from the given list that match the specified subjectId. */
export const getChaptersBySubjectId = (
  chapterIdsInfo: ChapterIdsInfo[],
  subjectId: number
): ChapterIdsInfo[] => {
  return chapterIdsInfo.filter((chapter) => chapter.subjectId === subjectId);
};

// =============================================================

export const convertToMultiSubjectFormateInfo = (
  dataToConvert: {
    subject: { name: string; id: number } | null;
    marks: number | undefined;
  }[],
  teachClassSubjects?: TeachClassSubjects[]
): MultiSubjectFormateInfo[] => {
  return dataToConvert
    .filter((data) => data.subject?.id != null && (data.marks ?? 0) > 0)
    .map((data) => {
      const subjectId = data.subject?.id;
      const subject =
        getTeacherSubjectEnum(subjectId, teachClassSubjects) ??
        Subject_SubjectEnum.UNDEFINED;
      return new MultiSubjectFormateInfo({
        subjectId,
        subject,
        preferredMarks: data.marks,
      });
    });
};

// =============================================================

export const buildTaskInfoModels = (
  multiSubjectSectionMetaInfo: MultiSubjectSection[]
): TaskInfoModel[] => {
  let seq = 1;
  const tasks: TaskInfoModel[] = [];
  for (const section of multiSubjectSectionMetaInfo ?? []) {
    for (const subSection of section.multiSubjectSubSections ?? []) {
      for (const q of subSection.subSectionMetaData ?? []) {
        if (!q?.questionId) continue;
        const task: TaskInfoModel = new TaskInfoModel({
          questionId: q.questionId,
          sequence: seq,
          maxMarks: q.maxMarks !== 0 ? q.maxMarks : undefined,
          sectionNo: section.sectionNo,
        });
        tasks.push(task);
        if (q.alternateQuestions) {
          // const totalAlternateQuestions = q.alternateQuestions.length;
          // const decimalPlaces = totalAlternateQuestions.toString().length;
          // let subSeq = 1;
          for (const altQ of q.alternateQuestions) {
            // const decimalValue = subSeq / Math.pow(10, decimalPlaces);
            if (!altQ.questionId) continue;
            const altTask: TaskInfoModel = new TaskInfoModel({
              questionId: altQ.questionId,
              // sequence: seq + decimalValue,
              sequence: seq,
              maxMarks: altQ.maxMarks !== 0 ? altQ.maxMarks : undefined,
              sectionNo: section.sectionNo,
            });
            tasks.push(altTask);
            // subSeq++;
          }
        }
        seq++;
      }
    }
  }
  return tasks;
};

// =============================================================

export const useDeleteQPGQuestion = () => {
  const {
    qpg_assessment_content,
    qpg_questions_sequence_info,
    qpg_question_module_map,
  } = deserify(useAppSelector((state) => state.assessment));
  const dispatch = useAppDispatch();
  const qpgAssessmentQuestions =
    qpg_assessment_content?.map((ques) => new Question(ques)) || [];
  const qpgQuestionsSequenceInfo = new TaskInfo(qpg_questions_sequence_info);
  const sectionMeta = qpgQuestionsSequenceInfo.questionSectionMetaInfo.sort(
    (a, b) => a.sectionNo - b.sectionNo
  ) as QuestionSectionMeta[];

  return (sectionIndex: number, questionIndex?: number, altIdx?: number) => {
    const section = sectionMeta[sectionIndex] as QuestionSectionMeta;
    if (!section) return;
    if (typeof questionIndex !== 'number') {
      return;
    }
    section.sectionQuestionsInfo = section.sectionQuestionsInfo.sort(
      (a, b) => a.sequence - b.sequence
    );
    const quesMeta = section.sectionQuestionsInfo[
      questionIndex
    ] as SectionQuestionMetaData;
    if (!quesMeta) return;
    const outerSequence = quesMeta.sequence;
    quesMeta.alternateQuestions = quesMeta.alternateQuestions.sort(
      (a, b) => a.sequence - b.sequence
    );
    const allQuestions = [
      quesMeta,
      ...quesMeta.alternateQuestions.sort((a, b) => a.sequence - b.sequence),
    ].map((val) => {
      val.alternateQuestions = [];
      return val;
    });
    let removed: SectionQuestionMetaData[] = [];
    if (typeof altIdx === 'number') {
      removed = allQuestions.splice(altIdx, 1);
    } else {
      // If altIdx is not provided, delete all
      removed = allQuestions.splice(0, allQuestions.length);
    }
    if (allQuestions.length > 0) {
      const newSectionQuestionMetaData = new SectionQuestionMetaData({
        ...allQuestions[0],
        alternateQuestions: allQuestions.slice(1).map((val, index) => ({
          ...val,
          sequence: index + 1,
        })),
        sequence: outerSequence,
      });
      section.sectionQuestionsInfo[questionIndex] = newSectionQuestionMetaData;
      sectionMeta[sectionIndex] = section;
      qpgQuestionsSequenceInfo.questionSectionMetaInfo = sectionMeta;
    } else {
      section.sectionQuestionsInfo.splice(questionIndex, 1);
      sectionMeta[sectionIndex] = section;
      qpgQuestionsSequenceInfo.questionSectionMetaInfo = sectionMeta;
    }
    const filteredQuestionIds = qpgQuestionsSequenceInfo.questionIds.filter(
      (id) => !removed.map((val) => val.questionId).includes(id)
    );
    qpgQuestionsSequenceInfo.questionIds = filteredQuestionIds;
    const filteredQuestionModuleMap = qpg_question_module_map?.filter(
      (val) =>
        !removed.map((val) => val.questionId).includes(val.questionId || '')
    );
    if (sectionMeta[sectionIndex].sectionQuestionsInfo.length === 0) {
      qpgQuestionsSequenceInfo.questionSectionMetaInfo.splice(sectionIndex, 1);
    }
    dispatch(setQPGQuestionsSequenceInfo(qpgQuestionsSequenceInfo));
    const filteredQpgAssessmentQuestions = qpgAssessmentQuestions.filter(
      (ques) =>
        !removed.map((val) => val.questionId).includes(ques.questionId || '')
    );
    dispatch(setQPGAssessmentContent(filteredQpgAssessmentQuestions));
    dispatch(setQPGQuestionModuleMap(filteredQuestionModuleMap || []));
  };
};

// =============================================================

/**
 * Merges two chapterTopicsInfo arrays by chapterId, then union of topics by topicId.
 * Preserves first-seen chapter/topic metadata (name, chapterNo, etc.).
 */

export const mergeChapterTopicsInfoUnion = (
  a: ChapterIdsInfo[],
  b: ChapterIdsInfo[]
): ChapterIdsInfo[] => {
  const byChapterId = new Map<
    number,
    { chapter: ChapterIdsInfo; topics: Map<number, TopicIdsInfo> }
  >();
  for (const c of [...a, ...b]) {
    const id = c.chapterId;
    if (!byChapterId.has(id)) {
      byChapterId.set(id, {
        chapter: c,
        topics: new Map(c.topicInfo.map((t) => [t.topicId, t])),
      });
    } else {
      const entry = byChapterId.get(id)!;
      for (const t of c.topicInfo) {
        entry.topics.set(t.topicId, t);
      }
    }
  }
  return Array.from(byChapterId.values()).map(
    ({ chapter, topics }) =>
      new ChapterIdsInfo({
        chapterId: chapter.chapterId,
        name: chapter.name,
        chapterNo: chapter.chapterNo,
        noOfQuestions: chapter.noOfQuestions,
        subjectId: chapter.subjectId,
        topicInfo: Array.from(topics.values()),
      })
  );
};

// =============================================================

const QUESTION_TYPE_TO_CONFIG_KEYS: ReadonlyArray<Question_QuestionEnum> = [
  Question_QuestionEnum.QUESTION_TYPE_MCQS,
  Question_QuestionEnum.QUESTION_TYPE_MCQM,
  Question_QuestionEnum.QUESTION_TYPE_FIB,
  Question_QuestionEnum.QUESTION_TYPE_TF,
  Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT,
  Question_QuestionEnum.QUESTION_TYPE_SHORT,
  Question_QuestionEnum.QUESTION_TYPE_LONG,
  Question_QuestionEnum.QUESTION_TYPE_CBQ,
];

export const getAvailableCountForQuestionType = (props: {
  questionType: Question_QuestionEnum;
  noOfQuestionsByTypes?: QuestionTypeCount;
}): number => {
  const { questionType, noOfQuestionsByTypes } = props;
  if (!noOfQuestionsByTypes) return Number.MAX_SAFE_INTEGER;
  const map: Partial<Record<Question_QuestionEnum, number>> = {
    [Question_QuestionEnum.QUESTION_TYPE_MCQS]: noOfQuestionsByTypes.noOfMcqs,
    [Question_QuestionEnum.QUESTION_TYPE_MCQM]: noOfQuestionsByTypes.noOfMcqm,
    [Question_QuestionEnum.QUESTION_TYPE_FIB]: noOfQuestionsByTypes.noOfFib,
    [Question_QuestionEnum.QUESTION_TYPE_TF]: noOfQuestionsByTypes.noOfTf,
    [Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT]:
      noOfQuestionsByTypes.noOfVeryShort,
    [Question_QuestionEnum.QUESTION_TYPE_SHORT]: noOfQuestionsByTypes.noOfShort,
    [Question_QuestionEnum.QUESTION_TYPE_LONG]: noOfQuestionsByTypes.noOfLong,
    [Question_QuestionEnum.QUESTION_TYPE_CBQ]: noOfQuestionsByTypes.noOfCbq,
  };
  const available = map[questionType];
  return typeof available === 'number' ? available : Number.MAX_SAFE_INTEGER;
};

// =============================================================

/**
 * Returns a mutable map of question type -> available count from config.
 * Used when building subsection data from template so we can allocate subsection-by-subsection (first subsection gets priority).
 */
export const getInitialRemainingByType = (
  noOfQuestionsByTypes: QuestionTypeCount | undefined
): Map<Question_QuestionEnum, number> => {
  const map = new Map<Question_QuestionEnum, number>();
  QUESTION_TYPE_TO_CONFIG_KEYS.forEach((questionType) => {
    map.set(
      questionType,
      getAvailableCountForQuestionType({
        questionType,
        noOfQuestionsByTypes,
      })
    );
  });
  return map;
};

// =============================================================

/**
 * Sum of noOfQuestion per question type across subsections.
 * If excludeSubSectionIndex is provided, that subsection is excluded (for "used in other subsections").
 */
export const getUsedCountByQuestionType = (
  subsectionData: SubSectionType[],
  excludeSubSectionIndex?: number
): Map<Question_QuestionEnum, number> => {
  const used = new Map<Question_QuestionEnum, number>();
  (subsectionData ?? []).forEach((sub, subIdx) => {
    if (
      excludeSubSectionIndex !== undefined &&
      subIdx === excludeSubSectionIndex
    )
      return;
    (sub.subSectionQuestions ?? []).forEach((q) => {
      const type =
        q.questionType ?? Question_QuestionEnum.QUESTION_TYPE_UNDEFINED;
      if (type === Question_QuestionEnum.QUESTION_TYPE_UNDEFINED) return;
      used.set(type, (used.get(type) ?? 0) + (q.noOfQuestion ?? 0));
    });
  });
  return used;
};

// =============================================================

/**
 * Remaining count per question type for a given subsection:
 * config count minus usage in all other subsections (excluding subSectionIndex).
 */
export const getRemainingForSubSection = (
  subsectionData: SubSectionType[],
  subSectionIndex: number,
  noOfQuestionsByTypes: QuestionTypeCount | undefined
): Map<Question_QuestionEnum, number> => {
  const remaining = new Map<Question_QuestionEnum, number>();
  if (!noOfQuestionsByTypes) {
    QUESTION_TYPE_TO_CONFIG_KEYS.forEach((t) =>
      remaining.set(t, Number.MAX_SAFE_INTEGER)
    );
    return remaining;
  }
  const usedInOthers = getUsedCountByQuestionType(
    subsectionData ?? [],
    subSectionIndex
  );
  QUESTION_TYPE_TO_CONFIG_KEYS.forEach((questionType) => {
    const total = getAvailableCountForQuestionType({
      questionType,
      noOfQuestionsByTypes,
    });
    const used = usedInOthers.get(questionType) ?? 0;
    remaining.set(questionType, Math.max(0, total - used));
  });
  return remaining;
};

// =============================================================

/** Question types allowed in AI mode (each subsection can have up to maxPerType per type). */
const AI_QUESTION_TYPES: ReadonlyArray<Question_QuestionEnum> = [
  Question_QuestionEnum.QUESTION_TYPE_MCQS,
  Question_QuestionEnum.QUESTION_TYPE_MCQM,
  Question_QuestionEnum.QUESTION_TYPE_FIB,
  Question_QuestionEnum.QUESTION_TYPE_TF,
  Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT,
  Question_QuestionEnum.QUESTION_TYPE_SHORT,
  Question_QuestionEnum.QUESTION_TYPE_LONG,
  Question_QuestionEnum.QUESTION_TYPE_CBQ,
];

/**
 * When allow_ai_questions is true: remaining per question type for a subsection
 * is (maxPerType - used in that subsection). Each subsection gets its own cap.
 */
export const getRemainingForSubSectionAIMode = (
  subsectionData: SubSectionType[],
  subSectionIndex: number,
  maxPerType = 25
): Map<Question_QuestionEnum, number> => {
  const remaining = new Map<Question_QuestionEnum, number>();
  const sub = subsectionData?.[subSectionIndex];
  const usedInThisSubsection = new Map<Question_QuestionEnum, number>();
  (sub?.subSectionQuestions ?? []).forEach((q) => {
    const type =
      q.questionType ?? Question_QuestionEnum.QUESTION_TYPE_UNDEFINED;
    if (type === Question_QuestionEnum.QUESTION_TYPE_UNDEFINED) return;
    usedInThisSubsection.set(
      type,
      (usedInThisSubsection.get(type) ?? 0) + (q.noOfQuestion ?? 0)
    );
  });
  AI_QUESTION_TYPES.forEach((questionType) => {
    const used = usedInThisSubsection.get(questionType) ?? 0;
    remaining.set(questionType, Math.max(0, maxPerType - used));
  });
  return remaining;
};

// =============================================================

/**
 * True if at least one question type has remaining capacity (total from config minus used across all subsections > 0).
 * Used to enable/disable "ADD SUB SECTION" when all types are exhausted.
 */
export const hasAnyQuestionTypeRemaining = (
  subsectionData: SubSectionType[],
  noOfQuestionsByTypes: QuestionTypeCount | undefined
): boolean => {
  if (!noOfQuestionsByTypes) return true;
  const used = getUsedCountByQuestionType(subsectionData ?? []);
  return QUESTION_TYPE_TO_CONFIG_KEYS.some((questionType) => {
    const total = getAvailableCountForQuestionType({
      questionType,
      noOfQuestionsByTypes,
    });
    if (total === Number.MAX_SAFE_INTEGER) return true;
    const usedCount = used.get(questionType) ?? 0;
    return usedCount < total;
  });
};

// =============================================================

export function areAllSubSectionQuestionsFilled(
  questions: SubSectionQuestionType[] | undefined
): boolean {
  if (!questions?.length) return true;
  return questions.every(
    (q) =>
      q.questionType !== undefined &&
      q.questionType !== Question_QuestionEnum.QUESTION_TYPE_UNDEFINED &&
      (q.marks ?? 0) > 0 &&
      (q.noOfQuestion ?? 0) > 0
  );
}

// =============================================================
